/**
 * Row-level hover for the bar charts.
 *
 * ECharts only fires item events on the painted rect, so a segment's hit area is exactly its ink. On
 * a stacked bar that is a problem: a stage worth 0.4 in a row totalling 129 is honestly about one
 * pixel wide, and nobody can put a cursor on it (RTLCA-5189). Inflating the ink to make it hittable
 * is the bug we just removed — it made the bar longer than its value — so the hit area has to stop
 * following the ink instead.
 *
 * The charts therefore also watch the grid for plain mouse movement, map the cursor to a category
 * with ECharts' own `convertFromPixel`, and hand that row to the existing hover callback. Segment
 * hover still wins wherever the cursor is genuinely over ink, because that event can name the stage;
 * this only fills in the rest of the row band.
 *
 * Kept as a dependency-free leaf module so it can be unit tested without pulling in ECharts or the
 * `@/features/Charts` barrel (which imports .vue components).
 */

export type RowHoverEntry = { value: number; id?: string } | undefined;

export type RowHover = {
  /** The category name, for the tooltip heading. */
  label: string;
  /** First id found for this row across the series, so callers can look the row up in their own data. */
  id?: string;
  /** Net total across every series at this category — positives and negatives together. */
  total: number;
  /** Per-series values at this category, in series order, for a breakdown tooltip. */
  values: number[];
};

/**
 * @param labels category names, indexed by category
 * @param seriesEntries data entries per series, each indexed by category — `seriesEntries[series][category]`
 * @param categoryIndex the row the cursor is over, as returned by `convertFromPixel` and rounded
 * @returns the row under the cursor, or null when the index falls outside the categories
 */
export function getRowHover(
  labels: string[],
  seriesEntries: ReadonlyArray<ReadonlyArray<RowHoverEntry>>,
  categoryIndex: number
): RowHover | null {
  if (!Number.isInteger(categoryIndex) || categoryIndex < 0 || categoryIndex >= labels.length) return null;

  const values = seriesEntries.map((entries) => entries[categoryIndex]?.value ?? 0);
  const id = seriesEntries.map((entries) => entries[categoryIndex]?.id).find((candidate) => !!candidate);

  return {
    label: labels[categoryIndex],
    id,
    total: values.reduce((sum, value) => sum + value, 0),
    values,
  };
}
